import { useState } from "react";
import useSWR from "swr";
import { BarChart3 } from "lucide-react";
import LeadsPanel from "@/pages/admin/LeadsPanel";
import PaymentsPanel from "@/pages/admin/PaymentsPanel";
import { ProductsPanel, CategoriesPanel, PostsPanel, FaqsPanel, CertificatesPanel, TestimonialsPanel, DocumentsPanel } from "@/pages/admin/ContentPanels";
import SettingsPanel from "@/pages/admin/SettingsPanel";
import { adminApi } from "@/lib/api";

const TABS = [
  { key: "leads", label: "Leads" },
  { key: "payments", label: "Payments" },
  { key: "products", label: "Products" },
  { key: "categories", label: "Categories" },
  { key: "testimonials", label: "Testimonials" },
  { key: "documents", label: "Documents" },
  { key: "posts", label: "Insights" },
  { key: "faqs", label: "FAQs" },
  { key: "certificates", label: "Certificates" },
  { key: "settings", label: "Settings" },
  { key: "activity", label: "Activity" },
];

const eventsFetcher = (url) => adminApi.get(url);

export default function AdminDashboard() {
  const [tab, setTab] = useState("leads");

  return (
    <div className="container-x py-6" data-testid="admin-dashboard">
      <div className="no-scrollbar mb-6 flex gap-2 overflow-x-auto border-b border-line pb-3">
        {TABS.map((t) => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            data-testid={`admin-tab-${t.key}`}
            className={`shrink-0 rounded-full border px-4 py-2 text-xs font-bold uppercase tracking-wider transition-colors ${tab === t.key ? "border-navy bg-navy text-ivory" : "border-line bg-white text-navy hover:border-gold"}`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === "leads" && <LeadsPanel />}
      {tab === "payments" && <PaymentsPanel />}
      {tab === "products" && <ProductsPanel />}
      {tab === "categories" && <CategoriesPanel />}
      {tab === "testimonials" && <TestimonialsPanel />}
      {tab === "documents" && <DocumentsPanel />}
      {tab === "posts" && <PostsPanel />}
      {tab === "faqs" && <FaqsPanel />}
      {tab === "certificates" && <CertificatesPanel />}
      {tab === "settings" && <SettingsPanel />}
      {tab === "activity" && <ActivityPanel />}
    </div>
  );
}

function ActivityPanel() {
  const { data } = useSWR("/admin/events", eventsFetcher, { refreshInterval: 15000 });
  return (
    <div className="grid gap-6 lg:grid-cols-2" data-testid="admin-activity-panel">
      <div className="card p-6">
        <h2 className="flex items-center gap-2 font-serif text-lg font-semibold text-navy"><BarChart3 size={17} className="text-gold-dark" /> Tracked Events</h2>
        {(data?.counts || []).length === 0 ? (
          <p className="mt-3 text-sm text-mist">No activity tracked yet. Quote submissions, WhatsApp clicks and catalogue downloads appear here.</p>
        ) : (
          <ul className="mt-4 space-y-2 text-sm">
            {data.counts.map((c) => (
              <li key={c.name} className="flex items-center justify-between border-b border-line pb-2">
                <span className="font-semibold text-navy">{c.name}</span>
                <span className="chip">{c.count}</span>
              </li>
            ))}
          </ul>
        )}
      </div>
      <div className="card p-6">
        <h2 className="font-serif text-lg font-semibold text-navy">Recent Activity</h2>
        <ul className="mt-4 space-y-2 text-xs text-mist">
          {(data?.recent || []).map((e, i) => (
            <li key={i} className="border-b border-line pb-2">
              <span className="font-bold text-navy">{e.name}</span> · {e.path || "/"} · {new Date(e.created_at).toLocaleString()}
            </li>
          ))}
          {(data?.recent || []).length === 0 && <li>No recent activity.</li>}
        </ul>
      </div>
    </div>
  );
}
